import { css } from 'styled-components';

/**
 * Screening design tokens (v3).
 *
 * These alias existing HireMe brand values where they exist (navy, fonts,
 * canvas, hairline) so the product reads as HireMe, and add the Screening-only
 * semantics the v3 language needs (desaturated status washes, spacing scale,
 * near-square radii, one overlay shadow). They never mutate the global or
 * `--agency-*` variables.
 */
const ScreeningVariables = css`
  :root {
    /* Palette (brand-aligned) */
    --screening-ink: #10151c;
    --screening-navy: #032170;
    --screening-navy-deep: #041a4d;
    --screening-blue-grey: #bacdd1;
    --screening-paper: #ffffff;
    --screening-canvas: #f6f8f9;
    --screening-hairline: #e4e8ec;
    --screening-muted: #5c6773;

    /* Desaturated, print-like status palette (never neon) */
    --screening-advance: #1b6b52;
    --screening-advance-wash: #eaf1ee;
    --screening-hold: #8a6a1b;
    --screening-hold-wash: #f4efe2;
    --screening-reject: #9a3934;
    --screening-reject-wash: #f3e7e6;

    /* Typography — unique family names + static files so a local or
       variable Space Grotesk / DM Sans cannot override the face.
       The @font-face rules live in _document. Keeping them in this
       styled-components sheet makes the browser drop the faces every
       time a new style is injected, which shifts the header. */
    --screening-font-display: 'HireMe Space Grotesk', 'HireMe DM Sans',
      sans-serif;
    --screening-font-ui: 'HireMe DM Sans', 'Inter', sans-serif;

    /* 8-point spacing scale */
    --screening-s1: 4px;
    --screening-s2: 8px;
    --screening-s3: 12px;
    --screening-s4: 16px;
    --screening-s6: 24px;
    --screening-s8: 32px;
    --screening-s12: 48px;
    --screening-s16: 64px;
    --screening-s24: 96px;

    /* Softened near-square radii — buttons a step rounder than fields */
    --screening-r-card: 8px;
    --screening-r-input: 6px;
    --screening-r-chip: 8px;
    --screening-r-button: 8px;

    /* Exactly one shadow, reserved for true floating overlays */
    --screening-shadow-overlay: 0 12px 32px -12px rgba(4, 26, 77, 0.28);

    /* The evidence spine */
    --screening-spine-width: 2px;
    --screening-spine-gap: 16px;
  }

  .screening-root {
    font-family: var(--screening-font-ui);
    color: var(--screening-ink);
    background: var(--screening-canvas);
    font-synthesis: none;
    font-variant-ligatures: none;
    font-feature-settings:
      'liga' 0,
      'calt' 0,
      'dlig' 0;
  }

  .screening-root *,
  .screening-root *::before,
  .screening-root *::after {
    box-sizing: border-box;
  }

  /* Tabular lining figures for any numeric run (scores, weights, money) */
  .screening-tnum {
    font-feature-settings:
      'tnum' 1,
      'lnum' 1;
    font-variant-numeric: tabular-nums lining-nums;
  }

  /* Thin, flat scrollbars scoped to screening (overrides the heavy 16px
     skeuomorphic global bars, most visible inside resized textareas). */
  html:has(.screening-root),
  html:has(.screening-root) body,
  .screening-root {
    scrollbar-width: thin;
    scrollbar-color: var(--screening-hairline) transparent;
  }

  html:has(.screening-root)::-webkit-scrollbar,
  html:has(.screening-root) body::-webkit-scrollbar,
  .screening-root *::-webkit-scrollbar,
  .screening-root::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }

  html:has(.screening-root)::-webkit-scrollbar-track,
  html:has(.screening-root) body::-webkit-scrollbar-track,
  .screening-root *::-webkit-scrollbar-track,
  .screening-root::-webkit-scrollbar-track {
    background: transparent;
  }

  html:has(.screening-root)::-webkit-scrollbar-thumb,
  html:has(.screening-root) body::-webkit-scrollbar-thumb,
  .screening-root *::-webkit-scrollbar-thumb,
  .screening-root::-webkit-scrollbar-thumb {
    background: var(--screening-hairline);
    border-radius: 999px;
    box-shadow: none;
  }

  html:has(.screening-root)::-webkit-scrollbar-thumb:hover,
  html:has(.screening-root) body::-webkit-scrollbar-thumb:hover,
  .screening-root *::-webkit-scrollbar-thumb:hover,
  .screening-root::-webkit-scrollbar-thumb:hover {
    background: #cdd4da;
  }
`;

export default ScreeningVariables;
